import { SeededRNG } from '../utils/SeededRNG.js';
import { DebugLog } from '../core/DebugLog.js';

// Tile type constants
export const TILE_VOID = 0;
export const TILE_FLOOR = 1;
export const TILE_WALL = 2;
export const TILE_DOOR = 3;
export const TILE_CORRIDOR_FLOOR = 4;
export const TILE_STAIRS = 5;

// Walkable tile set for collision checks
export const WALKABLE_TILES = new Set([TILE_FLOOR, TILE_DOOR, TILE_CORRIDOR_FLOOR, TILE_STAIRS]);

// Enemy type pool for spawning
const ENEMY_TYPES = ['tomb_rat', 'skeleton', 'skeleton_archer', 'mummy_guardian'];

/**
 * BSP node for recursive space partitioning.
 */
class BSPNode {
  constructor(x, y, width, height) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
    this.left = null;
    this.right = null;
    this.room = null;
  }

  get isLeaf() {
    return this.left === null && this.right === null;
  }
}

/**
 * BSP-based procedural dungeon generator.
 * Produces a 2D tile grid with rooms connected by L-shaped corridors.
 */
export class DungeonGenerator {

  /**
   * Generate a dungeon.
   * @param {number} width - Grid width in tiles
   * @param {number} height - Grid height in tiles
   * @param {number} seed - RNG seed
   * @param {number} [floor=1] - Current floor number (affects dungeon size)
   * @returns {{ tiles: number[][], rooms: object[], corridors: object[] }}
   */
  static generate(width, height, seed, floor = 1) {
    // Scale dungeon size with floor, capped at 100x70
    const scaledWidth = Math.min(100, width + (floor - 1) * 5);
    const scaledHeight = Math.min(70, height + (floor - 1) * 3);

    const rng = new SeededRNG(seed);
    const tiles = DungeonGenerator._createGrid(scaledWidth, scaledHeight);
    const rooms = [];
    const corridors = [];

    // Build BSP tree (leave 1-tile border around the entire map)
    const root = new BSPNode(1, 1, scaledWidth - 2, scaledHeight - 2);
    DungeonGenerator._split(root, rng);

    // Create rooms in leaf nodes
    DungeonGenerator._createRooms(root, rng, rooms);

    // Carve rooms into the tile grid
    for (const room of rooms) {
      DungeonGenerator._carveRoom(tiles, room);
    }

    // Connect sibling rooms with corridors
    DungeonGenerator._connectNodes(root, rng, tiles, corridors);

    // Add walls around all floor/corridor tiles
    DungeonGenerator._addWalls(tiles, scaledWidth, scaledHeight);

    // Mark entrance and boss rooms
    if (rooms.length > 0) {
      rooms[0].type = 'entrance';
      rooms[rooms.length - 1].type = 'boss';

      // Assign a treasure room (M21 relic chance)
      if (rooms.length >= 5) {
        // Pick a standard room that is not entrance/boss and not the shop candidate
        const treasureIdx = Math.max(1, Math.min(rooms.length - 2, Math.floor(rooms.length * 0.3)));
        const treasureRoom = rooms[treasureIdx];
        if (treasureRoom.type === 'standard') {
          treasureRoom.type = 'treasure';
          // 30% chance to contain a relic instead of regular loot
          if (rng.next() < 0.3) {
            treasureRoom.relicDrop = true;
          }
        }
      }

      // Assign one standard room as a shop room (if enough rooms exist)
      if (rooms.length >= 4) {
        // Pick a room that isn't entrance or boss (prefer middle rooms)
        const midIdx = Math.max(1, Math.min(rooms.length - 2, Math.floor(rooms.length / 2)));
        const shopRoom = rooms[midIdx];
        if (shopRoom.type === 'standard') {
          shopRoom.type = 'shop';
          shopRoom.npc = {
            type: 'merchant',
            x: shopRoom.centerX,
            y: shopRoom.centerY,
          };
        }
      }

      // Ensure boss room is at least 8x8 by expanding it if needed
      DungeonGenerator._ensureBossRoomSize(tiles, rooms[rooms.length - 1], scaledWidth, scaledHeight);

      // Place stairs in the center of the boss room
      const bossRoom = rooms[rooms.length - 1];
      tiles[bossRoom.centerY][bossRoom.centerX] = TILE_STAIRS;
    }

    // Spawn enemies in standard rooms
    DungeonGenerator._spawnEnemies(rooms, tiles, rng);

    // Place map objects (decorative and interactive) in rooms
    const mapObjects = DungeonGenerator._placeMapObjects(rooms, tiles, rng);

    DebugLog.info('DungeonGenerator', `Generated dungeon: ${scaledWidth}x${scaledHeight}, ${rooms.length} rooms, ${corridors.length} corridors, ${mapObjects.length} objects, seed=${seed}, floor=${floor}`);

    return { tiles, rooms, corridors, mapObjects, width: scaledWidth, height: scaledHeight };
  }

  /**
   * Ensure the boss room is at least 8x8. If not, expand it and re-carve.
   */
  static _ensureBossRoomSize(tiles, bossRoom, mapWidth, mapHeight) {
    const minSize = 8;

    if (bossRoom.width >= minSize && bossRoom.height >= minSize) return;

    // Calculate how much to expand
    const needW = Math.max(0, minSize - bossRoom.width);
    const needH = Math.max(0, minSize - bossRoom.height);

    // Try to expand evenly in both directions, clamped to map bounds (with 1-tile border)
    const expandLeft = Math.floor(needW / 2);
    const expandRight = needW - expandLeft;
    const expandUp = Math.floor(needH / 2);
    const expandDown = needH - expandUp;

    const newX = Math.max(1, bossRoom.x - expandLeft);
    const newY = Math.max(1, bossRoom.y - expandUp);
    const newW = Math.min(mapWidth - 2, bossRoom.width + needW);
    const newH = Math.min(mapHeight - 2, bossRoom.height + needH);

    // Make sure we don't exceed the map boundary on the right/bottom
    const finalW = Math.min(newW, mapWidth - 1 - newX);
    const finalH = Math.min(newH, mapHeight - 1 - newY);

    bossRoom.x = newX;
    bossRoom.y = newY;
    bossRoom.width = finalW;
    bossRoom.height = finalH;
    bossRoom.centerX = Math.floor(newX + finalW / 2);
    bossRoom.centerY = Math.floor(newY + finalH / 2);

    // Re-carve the expanded boss room
    DungeonGenerator._carveRoom(tiles, bossRoom);

    // Re-add walls around the newly carved tiles
    for (let row = Math.max(0, bossRoom.y - 1); row <= Math.min(mapHeight - 1, bossRoom.y + bossRoom.height); row++) {
      for (let col = Math.max(0, bossRoom.x - 1); col <= Math.min(mapWidth - 1, bossRoom.x + bossRoom.width); col++) {
        if (tiles[row][col] === TILE_VOID) {
          // Check if adjacent to any walkable tile
          let adjWalkable = false;
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) continue;
              const nr = row + dr;
              const nc = col + dc;
              if (nr >= 0 && nr < mapHeight && nc >= 0 && nc < mapWidth) {
                const walkable = new Set([TILE_FLOOR, TILE_CORRIDOR_FLOOR, TILE_DOOR, TILE_STAIRS]);
                if (walkable.has(tiles[nr][nc])) {
                  adjWalkable = true;
                }
              }
            }
          }
          if (adjWalkable) {
            tiles[row][col] = TILE_WALL;
          }
        }
      }
    }
  }

  /**
   * Create an empty grid filled with VOID.
   */
  static _createGrid(width, height) {
    const tiles = [];
    for (let row = 0; row < height; row++) {
      tiles[row] = new Array(width).fill(TILE_VOID);
    }
    return tiles;
  }

  /**
   * Recursively split a BSP node until partitions are small enough.
   * Minimum partition dimension: 8 tiles. Maximum: 15 tiles.
   */
  static _split(node, rng) {
    // Stop splitting if partition is already in the target range
    if (node.width <= 15 && node.height <= 15) {
      // Only split if both dimensions are large enough to produce valid children
      const canSplitH = node.height >= 16;
      const canSplitV = node.width >= 16;

      if (!canSplitH && !canSplitV) {
        return; // Leaf node
      }
    }

    // Decide split direction
    let splitHorizontally;
    const canSplitH = node.height >= 16;
    const canSplitV = node.width >= 16;

    if (canSplitH && canSplitV) {
      // Prefer splitting the longer axis, with some randomness
      if (node.width / node.height >= 1.25) {
        splitHorizontally = false;
      } else if (node.height / node.width >= 1.25) {
        splitHorizontally = true;
      } else {
        splitHorizontally = rng.nextBool();
      }
    } else if (canSplitH) {
      splitHorizontally = true;
    } else if (canSplitV) {
      splitHorizontally = false;
    } else {
      return; // Cannot split further
    }

    if (splitHorizontally) {
      // Split along Y axis
      const minSplit = 8;
      const maxSplit = node.height - 8;
      if (minSplit > maxSplit) return;

      const split = rng.nextInt(minSplit, maxSplit);
      node.left = new BSPNode(node.x, node.y, node.width, split);
      node.right = new BSPNode(node.x, node.y + split, node.width, node.height - split);
    } else {
      // Split along X axis
      const minSplit = 8;
      const maxSplit = node.width - 8;
      if (minSplit > maxSplit) return;

      const split = rng.nextInt(minSplit, maxSplit);
      node.left = new BSPNode(node.x, node.y, split, node.height);
      node.right = new BSPNode(node.x + split, node.y, node.width - split, node.height);
    }

    DungeonGenerator._split(node.left, rng);
    DungeonGenerator._split(node.right, rng);
  }

  /**
   * Create rooms inside leaf nodes of the BSP tree.
   */
  static _createRooms(node, rng, rooms) {
    if (node.isLeaf) {
      // Room must be padded 1 tile from partition edge, and 4-12 tiles in size
      const maxW = Math.min(12, node.width - 2);
      const maxH = Math.min(12, node.height - 2);
      const minW = Math.min(4, maxW);
      const minH = Math.min(4, maxH);

      if (maxW < minW || maxH < minH) return;

      const roomW = rng.nextInt(minW, maxW);
      const roomH = rng.nextInt(minH, maxH);

      // Position room within partition with 1-tile padding
      const roomX = rng.nextInt(node.x + 1, node.x + node.width - roomW - 1);
      const roomY = rng.nextInt(node.y + 1, node.y + node.height - roomH - 1);

      const room = {
        x: roomX,
        y: roomY,
        width: roomW,
        height: roomH,
        centerX: Math.floor(roomX + roomW / 2),
        centerY: Math.floor(roomY + roomH / 2),
        type: 'standard',
      };

      node.room = room;
      rooms.push(room);
      return;
    }

    if (node.left) DungeonGenerator._createRooms(node.left, rng, rooms);
    if (node.right) DungeonGenerator._createRooms(node.right, rng, rooms);
  }

  /**
   * Carve a room into the tile grid (set tiles to FLOOR).
   */
  static _carveRoom(tiles, room) {
    for (let row = room.y; row < room.y + room.height; row++) {
      for (let col = room.x; col < room.x + room.width; col++) {
        tiles[row][col] = TILE_FLOOR;
      }
    }
  }

  /**
   * Recursively connect sibling rooms via L-shaped corridors.
   */
  static _connectNodes(node, rng, tiles, corridors) {
    if (node.isLeaf) return;

    if (node.left) DungeonGenerator._connectNodes(node.left, rng, tiles, corridors);
    if (node.right) DungeonGenerator._connectNodes(node.right, rng, tiles, corridors);

    // Get a room from each subtree to connect
    const leftRoom = DungeonGenerator._getRoom(node.left, rng);
    const rightRoom = DungeonGenerator._getRoom(node.right, rng);

    if (leftRoom && rightRoom) {
      DungeonGenerator._carveCorridor(tiles, leftRoom, rightRoom, rng, corridors);
    }
  }

  /**
   * Get a random room from a subtree.
   */
  static _getRoom(node, rng) {
    if (node.room) return node.room;
    const rooms = [];
    DungeonGenerator._collectRooms(node, rooms);
    if (rooms.length === 0) return null;
    return rooms[rng.nextInt(0, rooms.length - 1)];
  }

  /**
   * Collect all rooms from a subtree.
   */
  static _collectRooms(node, rooms) {
    if (node.room) rooms.push(node.room);
    if (node.left) DungeonGenerator._collectRooms(node.left, rooms);
    if (node.right) DungeonGenerator._collectRooms(node.right, rooms);
  }

  /**
   * Carve an L-shaped corridor between two rooms.
   * The corridor width is 1-2 tiles.
   */
  static _carveCorridor(tiles, roomA, roomB, rng, corridors) {
    const x1 = roomA.centerX;
    const y1 = roomA.centerY;
    const x2 = roomB.centerX;
    const y2 = roomB.centerY;

    const corridorWidth = rng.nextInt(1, 2);
    const corridor = { x1, y1, x2, y2, width: corridorWidth };
    corridors.push(corridor);

    // Decide L-shape direction: go horizontal first or vertical first
    if (rng.nextBool()) {
      // Horizontal then vertical
      DungeonGenerator._carveHLine(tiles, x1, x2, y1, corridorWidth);
      DungeonGenerator._carveVLine(tiles, y1, y2, x2, corridorWidth);
    } else {
      // Vertical then horizontal
      DungeonGenerator._carveVLine(tiles, y1, y2, x1, corridorWidth);
      DungeonGenerator._carveHLine(tiles, x1, x2, y2, corridorWidth);
    }
  }

  /**
   * Carve a horizontal corridor line.
   */
  static _carveHLine(tiles, x1, x2, y, width) {
    const startX = Math.min(x1, x2);
    const endX = Math.max(x1, x2);
    const height = tiles.length;
    const gridWidth = tiles[0].length;

    for (let x = startX; x <= endX; x++) {
      for (let w = 0; w < width; w++) {
        const ty = y + w;
        if (ty >= 0 && ty < height && x >= 0 && x < gridWidth) {
          if (tiles[ty][x] === TILE_VOID) {
            tiles[ty][x] = TILE_CORRIDOR_FLOOR;
          }
        }
      }
    }
  }

  /**
   * Carve a vertical corridor line.
   */
  static _carveVLine(tiles, y1, y2, x, width) {
    const startY = Math.min(y1, y2);
    const endY = Math.max(y1, y2);
    const height = tiles.length;
    const gridWidth = tiles[0].length;

    for (let y = startY; y <= endY; y++) {
      for (let w = 0; w < width; w++) {
        const tx = x + w;
        if (y >= 0 && y < height && tx >= 0 && tx < gridWidth) {
          if (tiles[y][tx] === TILE_VOID) {
            tiles[y][tx] = TILE_CORRIDOR_FLOOR;
          }
        }
      }
    }
  }

  /**
   * Add walls around all floor and corridor tiles.
   * Also place DOOR tiles where corridors meet room edges.
   */
  static _addWalls(tiles, width, height) {
    // First pass: identify door positions (corridor tiles adjacent to room floor)
    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        if (tiles[row][col] === TILE_CORRIDOR_FLOOR) {
          // Check if this corridor tile is adjacent to a room floor tile
          const neighbors = [
            [row - 1, col], [row + 1, col],
            [row, col - 1], [row, col + 1],
          ];
          for (const [nr, nc] of neighbors) {
            if (nr >= 0 && nr < height && nc >= 0 && nc < width) {
              if (tiles[nr][nc] === TILE_FLOOR) {
                // This corridor tile borders a room -- mark as door
                tiles[row][col] = TILE_DOOR;
                break;
              }
            }
          }
        }
      }
    }

    // Second pass: add walls around walkable tiles
    const walkable = new Set([TILE_FLOOR, TILE_CORRIDOR_FLOOR, TILE_DOOR]);
    // Collect wall positions first to avoid mutation during iteration
    const wallPositions = [];

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        if (tiles[row][col] === TILE_VOID) {
          // Check if adjacent to any walkable tile (8-directional)
          let adjWalkable = false;
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) continue;
              const nr = row + dr;
              const nc = col + dc;
              if (nr >= 0 && nr < height && nc >= 0 && nc < width) {
                if (walkable.has(tiles[nr][nc])) {
                  adjWalkable = true;
                }
              }
            }
          }
          if (adjWalkable) {
            wallPositions.push([row, col]);
          }
        }
      }
    }

    for (const [row, col] of wallPositions) {
      tiles[row][col] = TILE_WALL;
    }
  }

  /**
   * Spawn enemy data in standard rooms.
   * Entrance and boss rooms get no enemies.
   * Each standard room gets 2-4 enemies at random walkable positions.
   */
  static _spawnEnemies(rooms, tiles, rng) {
    for (const room of rooms) {
      if (room.type === 'entrance' || room.type === 'boss' || room.type === 'shop' || room.type === 'treasure') {
        room.enemies = [];
        continue;
      }

      const enemyCount = rng.nextInt(2, 4);
      const enemies = [];

      // Collect walkable tiles in this room (excluding center to avoid stacking)
      const walkableTiles = [];
      for (let row = room.y; row < room.y + room.height; row++) {
        for (let col = room.x; col < room.x + room.width; col++) {
          // Skip the exact center tile (player may pass through)
          if (col === room.centerX && row === room.centerY) continue;
          if (WALKABLE_TILES.has(tiles[row][col])) {
            walkableTiles.push({ col, row });
          }
        }
      }

      for (let i = 0; i < enemyCount && walkableTiles.length > 0; i++) {
        const idx = rng.nextInt(0, walkableTiles.length - 1);
        const tile = walkableTiles.splice(idx, 1)[0];
        const type = ENEMY_TYPES[rng.nextInt(0, ENEMY_TYPES.length - 1)];

        enemies.push({
          type,
          x: tile.col,
          y: tile.row,
        });
      }

      room.enemies = enemies;
    }
  }

  /**
   * Place map objects in rooms: torches, barrels, pillars (decorative),
   * chests (treasure rooms), shrines, merchant carts (shop rooms),
   * stairs and doors are handled by tile types.
   */
  static _placeMapObjects(rooms, tiles, rng) {
    const objects = [];

    for (const room of rooms) {
      // Collect wall-adjacent floor tiles for placing decorative objects
      const wallAdjacentTiles = [];
      const innerTiles = [];

      for (let row = room.y; row < room.y + room.height; row++) {
        for (let col = room.x; col < room.x + room.width; col++) {
          if (!WALKABLE_TILES.has(tiles[row][col])) continue;
          if (col === room.centerX && row === room.centerY) continue;

          // Check if adjacent to a wall
          let nearWall = false;
          for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]]) {
            const nr = row + dr, nc = col + dc;
            if (nr >= 0 && nr < tiles.length && nc >= 0 && nc < tiles[0].length) {
              if (tiles[nr][nc] === TILE_WALL) nearWall = true;
            }
          }

          if (nearWall) {
            wallAdjacentTiles.push({ col, row });
          } else {
            innerTiles.push({ col, row });
          }
        }
      }

      // Place objects based on room type
      if (room.type === 'treasure') {
        // Chest in the center
        objects.push({ type: 'chest', x: room.centerX, y: room.centerY });

        // 1-2 torches near walls
        DungeonGenerator._placeDecorations(objects, wallAdjacentTiles, rng, ['torch'], 1, 2);
      } else if (room.type === 'shop') {
        // Merchant cart near the merchant NPC (offset by 1 tile)
        const cartX = Math.min(room.centerX + 1, room.x + room.width - 1);
        objects.push({ type: 'merchant_cart', x: cartX, y: room.centerY });

        // Torches for ambiance
        DungeonGenerator._placeDecorations(objects, wallAdjacentTiles, rng, ['torch'], 2, 3);
      } else if (room.type === 'entrance') {
        // Shrine in entrance room
        if (innerTiles.length > 0) {
          const pos = innerTiles[rng.nextInt(0, innerTiles.length - 1)];
          objects.push({ type: 'shrine', x: pos.col, y: pos.row });
        }
        // Torches
        DungeonGenerator._placeDecorations(objects, wallAdjacentTiles, rng, ['torch'], 2, 3);
      } else if (room.type === 'boss') {
        // Pillars and torches in boss room
        DungeonGenerator._placeDecorations(objects, wallAdjacentTiles, rng, ['pillar', 'torch'], 3, 5);
      } else {
        // Standard rooms: random decorations (barrels, torches, pillars)
        const count = rng.nextInt(0, 3);
        if (count > 0) {
          DungeonGenerator._placeDecorations(objects, wallAdjacentTiles, rng, ['barrel', 'torch', 'pillar'], 1, count);
        }
      }
    }

    return objects;
  }

  /**
   * Place decorative objects at random wall-adjacent positions.
   */
  static _placeDecorations(objects, candidates, rng, types, minCount, maxCount) {
    if (candidates.length === 0) return;

    const count = Math.min(rng.nextInt(minCount, maxCount), candidates.length);
    const available = [...candidates];

    for (let i = 0; i < count && available.length > 0; i++) {
      const idx = rng.nextInt(0, available.length - 1);
      const pos = available.splice(idx, 1)[0];
      const type = types[rng.nextInt(0, types.length - 1)];
      objects.push({ type, x: pos.col, y: pos.row });
    }
  }
}
